import styled from '@emotion/styled'
import { Trans } from '@lingui/macro'
import * as React from 'react'
import { CSSTransition } from 'react-transition-group'

import { minWidth } from '@emico/styles'

import ProductInfoFormMinimal from './ProductInfoFormMinimal'
import Container from '../../../layout/Container'
import { getBreadcrumbs } from '../../../navigation/ProductBreadcrumbs'
import PriceBox, {
    getConfigurableProductPrices,
    getPrices,
} from '../../../presentation/PriceBox'
import Heading from '../../../typography/Heading'
import Text from '../../../typography/Text'
import getProductStockInfo from '../../../utils/getProductStockInfo'
import { useRootCategoryId } from '../../useRootCategory'
import { setSelectedConfigurableValue } from '../ConfigurableAttributesField/ConfigurableAttributesField'
import { ConfigurableProduct } from '../ConfigurableProduct'
import { isGroupedProduct, Product } from '../GetProduct.query'

interface Props {
    product: Product
    showSticky: boolean
}

const Cta = styled.div`
    position: fixed;
    z-index: 999;
    bottom: 0;
    left: 0;
    width: 100vw;
    background-color: white;
    box-shadow: 0 10px 20px black;
    transition:
        opacity 300ms,
        transform 300ms;
    will-change: opacity, transform;
    opacity: 0;
    transform: translate3d(0, 100%, 0);

    &.stickyCtaTransition-enter {
        opacity: 0;
        transform: translate3d(0, 100%, 0);
    }

    &.stickyCtaTransition-enter-done,
    &.stickyCtaTransition-enter-active {
        opacity: 1;
        transform: translate3d(0, 0%, 0);
    }

    &.stickyCtaTransition-exit {
        opacity: 1;
        transform: translate3d(0, 0%, 0);
    }

    &.stickyCtaTransition-exit-done,
    &.stickyCtaTransition-exit-active {
        opacity: 0;
        transform: translate3d(0, 100%, 0);
    }
`

const ProductInfo = styled.div`
    display: none;
    min-height: 40px;
    flex-basis: 49%;
    flex-grow: 1;
    @media ${minWidth('lg')} {
        display: block;
        flex-basis: 33%;
    }
`

const CtaContainer = styled.div`
    display: flex;
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 30px;
`

const StickyCta = ({ product, showSticky }: Props) => {
    const rootCategoryId = useRootCategoryId()

    if (isGroupedProduct(product)) {
        return null
    }

    const isConfigurable = Boolean(
        (product as ConfigurableProduct).configurableOptions,
    )

    const prices = isConfigurable
        ? getConfigurableProductPrices(product as ConfigurableProduct)
        : getPrices(product)

    if (!rootCategoryId) {
        return null
    }

    const breadcrumbs = getBreadcrumbs(
        product.categories,
        rootCategoryId,
        product.name,
        product.urlKey,
    )

    if (breadcrumbs.length === 0) {
        return null
    }

    const { label: mainCategoryLabel } = breadcrumbs[0]

    const { isOutOfStock } = getProductStockInfo(product)

    return (
        <>
            <CSSTransition
                in={showSticky}
                timeout={5000}
                classNames="stickyCtaTransition"
            >
                <Cta>
                    <Container>
                        <CtaContainer>
                            <ProductInfo>
                                <Heading element="h1" color="dark" variant="h3">
                                    {product.name}
                                </Heading>
                                {isOutOfStock ? (
                                    <Text>
                                        <Trans id="core.outOfStockInfo.text">
                                            Sorry, unfortunately this product is
                                            out of stock. But we still have some
                                            other amazing {mainCategoryLabel}{' '}
                                            that you may like.
                                        </Trans>
                                    </Text>
                                ) : (
                                    <>
                                        {prices && (
                                            <PriceBox
                                                {...prices}
                                                variantBig
                                                outletAdviceprice={
                                                    product.outletAdviesprijs ??
                                                    null
                                                }
                                            />
                                        )}
                                    </>
                                )}
                            </ProductInfo>
                            <ProductInfoFormMinimal
                                product={product}
                                onValueChange={(value) => {
                                    setSelectedConfigurableValue(product, value)
                                }}
                            />
                        </CtaContainer>
                    </Container>
                </Cta>
            </CSSTransition>
        </>
    )
}

export default StickyCta
